/**
 * Manages the state and actions related to including HTML content in the application.
 *
 * The `includes` module provides functionality to load and store HTML content that is used throughout the application, such as introductions, thank you messages, platform information, and more.
 *
 * The module uses Axios to fetch the HTML content from local and global paths, and stores the content in the Vuex store. The `getInclude` getter can be used to retrieve the content by name.
 *
 * The `loadAllIncludes` action can be used to load all the includes at once, while the `loadInclude` action can be used to load a single include.
 *
 * The `setInclude` mutation is used to update the state with the loaded include content, and the `setIncludes` mutation can be used to set the entire includes object.
 */

import axios from "axios";

/**
 * Cache-bust token for include fetches. Includes are fetched at runtime via
 * axios (XHR), which a browser hard-refresh does NOT bypass — so without a
 * versioned URL, browsers keep serving a stale cached copy of an include after
 * its content changes. Bump this whenever an include's content changes (same
 * convention as the ?v= on the T&C PDF link).
 */
const INCLUDES_CACHE_BUST = "2026-08-11";

/**
 * The state object that holds the HTML content for various includes used throughout the application.
 * 
 * The `includes` object contains the following properties:
 * - `intro`: The HTML content for the application introduction.
 * - `thanks`: The HTML content for the thank you message.
 * - `platforms_intro_available`: The HTML content for the platforms introduction when they are available.
 * - `platforms_intro_other`: The HTML content for the platforms introduction when they are not available.
 * - `additional_information_text`: The HTML content for additional information text.
 * - `platform_authorization_header`: The HTML content for the platform authorization header.
 * - `finio_terms_and_conditions`: The HTML content for the Finio terms and conditions.
 */
const state = {
  includes: {
    intro: "",
    thanks: "",
    platforms_intro_available: "",
    platforms_intro_other: "",
    additional_information_text: "",
    platform_authorization_header: "",
    finio_terms_and_conditions: "",
  },
};

/**
 * Retrieves the HTML content for the specified include by name.
 *
 * @param {Object} state - The Vuex store state.
 * @param {string} name - The name of the include to retrieve.
 * @returns {string} The HTML content for the specified include.
 */

const getters = {
  getInclude: (state) => (name) => {
    return state.includes[name] || '';
  },
};



const actions = {

  async loadAllIncludes({ dispatch }, { fileNames, tpspName }) {
    console.log("LoadAllInclude, tpsp name: ", tpspName);
    for (const fileName of fileNames) {
      await dispatch("loadInclude", { fileName, tpspName });
    }
  },

  async loadInclude({ commit }, { fileName, tpspName }) {
    const formattedFileName = fileName.replace(/_/g, "-");
    const cacheBust = "?v=" + INCLUDES_CACHE_BUST;
    let path_local = "includes/" + formattedFileName + ".html" + cacheBust;
    let path_global = "/global/includes/" + formattedFileName + ".html" + cacheBust;

    const axiosConfig = {
      silent: true,
      validateStatus: (status) => status < 500,
    };

    try {
      const localResponse = await axios.get(path_local, axiosConfig);
      if (localResponse.status === 200) {
        commit("setInclude", {
          name: fileName,
          content: localResponse.data,
          tpspName
        });
        return;
      }

      const globalResponse = await axios.get(path_global, axiosConfig);
      if (globalResponse.status === 200) {
        commit("setInclude", {
          name: fileName,
          content: globalResponse.data,
          tpspName
        });
        return;
      }

      // console.log(`Include ${fileName} not found in local or global path`);
    } catch (error) {
      // console.log(`Error loading globalinclude ${fileName}: ${error.message}`);
    }
  },
};


/**
 * Mutations for managing includes in the Vuex store.
 *
 * @param {Object} state - The Vuex store state.
 * @param {string} name - The name of the include.
 * @param {string} content - The content of the include.
 * @returns {void}
 */
const mutations = {
  setInclude(state, { name, content, tpspName }) {
    state.includes[name] = content.replace(/\{TPSP_Name\}/g, tpspName);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
